import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ShieldAlert, ShieldCheck, RefreshCw, ExternalLink } from "lucide-react";
import API from "../services/api";
import AdminLayout from "./AdminLayout";

const fmt = (n) => `₹${Number(n||0).toLocaleString("en-IN", { minimumFractionDigits: 0 })}`;
const fmtR = (n) => Number(n||0).toFixed(2);

const RISK_COLOR = { HIGH:"#dc2626", MEDIUM:"#f97316", LOW:"#16a34a" };
const RISK_BG    = { HIGH:"#fef2f2", MEDIUM:"#fff7ed", LOW:"#f0fdf4" };

function RiskBadge({ level }) {
  const Icon = level === "HIGH" ? ShieldAlert : level === "MEDIUM" ? AlertTriangle : ShieldCheck;
  return (
    <span style={{
      display:"inline-flex", alignItems:"center", gap:4, padding:"3px 10px",
      borderRadius:20, fontWeight:800, fontSize:12,
      background: RISK_BG[level]||"#f1f5f9", color: RISK_COLOR[level]||"#64748b",
      border:`1px solid ${RISK_COLOR[level]||"#94a3b8"}33`,
    }}>
      <Icon size={12}/>{level}
    </span>
  );
}

function ScoreBar({ score }) {
  const col = score >= 70 ? "#dc2626" : score >= 40 ? "#f97316" : "#16a34a";
  return (
    <div style={{display:"flex", alignItems:"center", gap:8}}>
      <div style={{flex:1, height:8, borderRadius:4, background:"#f1f5f9", overflow:"hidden"}}>
        <div style={{width:`${score}%`, height:"100%", background:col, borderRadius:4, transition:"width .4s"}}/>
      </div>
      <span style={{fontWeight:900, fontSize:13, color:col, minWidth:30}}>{score}</span>
    </div>
  );
}

export default function AdminRiskMonitor() {
  const navigate = useNavigate();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays]     = useState(30);
  const [filter, setFilter] = useState("ALL");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await API.get(`/admin/risk-monitor?days=${days}`);
      setData(r.data);
    } catch(e) { setData(null); }
    finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const users = (data?.users || []).filter(u => filter === "ALL" || u.risk_level === filter);
  const counts = { HIGH:0, MEDIUM:0, LOW:0 };
  (data?.users||[]).forEach(u => counts[u.risk_level]++);

  return (
    <AdminLayout>
      <div style={page}>

        {/* Header */}
        <div style={header}>
          <div>
            <div style={title}><ShieldAlert size={22} color="#dc2626" style={{marginRight:8}}/>Risk Monitor</div>
            <div style={subtitle}>Users flagged for high withdrawals, unusual wins, or suspicious activity</div>
          </div>
          <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
            <select style={sel} value={days} onChange={e=>setDays(Number(e.target.value))}>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>All time</option>
            </select>
            <button style={refreshBtn} onClick={load}>
              <RefreshCw size={15} style={{marginRight:4}}/> Refresh
            </button>
          </div>
        </div>

        {/* Summary cards */}
        <div style={summaryRow}>
          {[
            {label:"High Risk", count:counts.HIGH,   col:"#dc2626", bg:"#fef2f2", level:"HIGH"},
            {label:"Medium Risk",count:counts.MEDIUM, col:"#f97316", bg:"#fff7ed", level:"MEDIUM"},
            {label:"Low Risk",  count:counts.LOW,    col:"#16a34a", bg:"#f0fdf4", level:"LOW"},
            {label:"Total Users",count:(data?.users||[]).length, col:"#ea580c", bg:"#fff7ed", level:"ALL"},
          ].map(c=>(
            <button key={c.level} style={{...summaryCard, background:filter===c.level?c.bg:"#fff", borderColor:filter===c.level?c.col:"#e2e8f0"}}
              onClick={()=>setFilter(filter===c.level?"ALL":c.level)}>
              <div style={{fontSize:28,fontWeight:900,color:c.col}}>{c.count}</div>
              <div style={{fontSize:12,color:"#64748b",fontWeight:600}}>{c.label}</div>
            </button>
          ))}
        </div>

        {loading && <div style={loadBox}>Loading risk data…</div>}

        {!loading && users.length === 0 && (
          <div style={loadBox}>No users found for this filter.</div>
        )}

        {/* User risk cards */}
        {!loading && users.map(u => {
          const isOpen = expanded === u.id;
          const cNet = u.casino_won - u.casino_wagered;
          const mNet = u.matka_won - u.matka_wagered;
          return (
            <div key={u.id} style={{...riskCard, borderLeft:`4px solid ${RISK_COLOR[u.risk_level]||"#94a3b8"}`}}>
              {/* Top row */}
              <div style={cardTop}>
                <div style={userInfo}>
                  <div style={userName}>{u.name}</div>
                  <div style={userPhone}>{u.phone} · ID {u.id}</div>
                  {u.status === "Blocked" && <span style={blockedBadge}>BLOCKED</span>}
                </div>
                <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                  <RiskBadge level={u.risk_level}/>
                  <div style={{minWidth:120}}><ScoreBar score={u.risk_score}/></div>
                  <button style={viewBtn} onClick={()=>navigate(`/admin/users/${u.id}`)}>
                    <ExternalLink size={13}/> Profile
                  </button>
                  <button style={expandBtn} onClick={()=>setExpanded(isOpen?null:u.id)}>
                    {isOpen?"▲ Less":"▼ More"}
                  </button>
                </div>
              </div>

              {/* Key metrics row */}
              <div style={metricsRow}>
                <Metric label="Deposited"   val={fmt(u.total_deposits)}  col="#ea580c"/>
                <Metric label="Withdrawn"   val={fmt(u.total_withdrawn)}  col="#dc2626"/>
                <Metric label="WD/Dep Ratio" val={`${fmtR(u.wd_to_deposit_ratio)}×`} col={u.wd_to_deposit_ratio>1.5?"#dc2626":u.wd_to_deposit_ratio>1?"#f97316":"#16a34a"}/>
                <Metric label="Balance"     val={fmt(u.balance)}          col="#0891b2"/>
                <Metric label="True Balance" val={`${u.true_balance>=0?"":"-"}${fmt(Math.abs(u.true_balance))}`} col={u.true_balance>=0?"#16a34a":"#dc2626"}/>
                {u.pending_withdrawal > 0 && <Metric label="Pending WD" val={fmt(u.pending_withdrawal)} col="#f97316"/>}
              </div>

              {/* Flags */}
              {u.flags.length > 0 && (
                <div style={flagsRow}>
                  {u.flags.map((f,i)=>(
                    <span key={i} style={flagChip}><AlertTriangle size={10}/> {f}</span>
                  ))}
                </div>
              )}

              {/* Expanded detail */}
              {isOpen && (
                <div style={expandedPanel}>
                  <div style={expandGrid}>
                    <div style={expandSection}>
                      <div style={secTitle}>Casino</div>
                      <Row label="Bets placed" val={u.casino_bets}/>
                      <Row label="Total wagered" val={fmt(u.casino_wagered)}/>
                      <Row label="Total won" val={fmt(u.casino_won)}/>
                      <Row label="Net P&L" val={`${cNet>=0?"+":""}${fmt(cNet)}`} col={cNet>=0?"#16a34a":"#dc2626"}/>
                    </div>
                    <div style={expandSection}>
                      <div style={secTitle}>Matka</div>
                      <Row label="Bets placed" val={u.matka_bets}/>
                      <Row label="Bets won" val={`${u.matka_won_bets} (${u.matka_win_rate}%)`} col={u.matka_win_rate>15?"#dc2626":u.matka_win_rate>10?"#f97316":"#431407"}/>
                      <Row label="Total wagered" val={fmt(u.matka_wagered)}/>
                      <Row label="Total won" val={fmt(u.matka_won)}/>
                      <Row label="Payout ratio" val={`${u.matka_net_ratio}×`} col={u.matka_net_ratio>3?"#dc2626":u.matka_net_ratio>2?"#f97316":"#431407"}/>
                      <Row label="Biggest win" val={fmt(u.matka_biggest_win)}/>
                      <Row label="Net P&L" val={`${mNet>=0?"+":""}${fmt(mNet)}`} col={mNet>=0?"#16a34a":"#dc2626"}/>
                    </div>
                    <div style={expandSection}>
                      <div style={secTitle}>Recent ({data?.days}d)</div>
                      <Row label="Deposits" val={fmt(u.recent_deposits)}/>
                      <Row label="Withdrawals" val={fmt(u.recent_withdrawn)}/>
                      <Row label="WD count (total)" val={u.wd_count}/>
                      <Row label="Matka won" val={fmt(u.recent_matka_won)}/>
                      <Row label="Matka win rate" val={`${u.recent_matka_win_rate}%`} col={u.recent_matka_win_rate>20?"#dc2626":u.recent_matka_win_rate>15?"#f97316":"#431407"}/>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </AdminLayout>
  );
}

function Metric({ label, val, col }) {
  return (
    <div style={metricBox}>
      <div style={{fontSize:10,color:"#64748b",fontWeight:600,textTransform:"uppercase",letterSpacing:.5}}>{label}</div>
      <div style={{fontWeight:900,fontSize:15,color:col||"#431407"}}>{val}</div>
    </div>
  );
}
function Row({ label, val, col }) {
  return (
    <div style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:"1px solid #f1f5f9"}}>
      <span style={{fontSize:12,color:"#64748b"}}>{label}</span>
      <span style={{fontSize:12,fontWeight:800,color:col||"#431407"}}>{val}</span>
    </div>
  );
}

/* ── styles ── */
const page       = { padding:16, maxWidth:1100, margin:"0 auto" };
const header     = { display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:12, marginBottom:18 };
const title      = { display:"flex", alignItems:"center", fontSize:22, fontWeight:900, color:"#431407", marginBottom:4 };
const subtitle   = { fontSize:13, color:"#64748b" };
const summaryRow = { display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))", gap:10, marginBottom:18 };
const summaryCard= { padding:"14px 16px", borderRadius:12, border:"2px solid #e2e8f0", cursor:"pointer", textAlign:"left", background:"#fff", transition:"all .2s" };
const loadBox    = { textAlign:"center", padding:40, color:"#64748b", fontSize:15 };
const riskCard   = { background:"#fff", borderRadius:12, padding:16, marginBottom:12, boxShadow:"0 1px 6px #0001", border:"1px solid #f1f5f9" };
const cardTop    = { display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10, marginBottom:10 };
const userInfo   = { display:"flex", flexDirection:"column", gap:2 };
const userName   = { fontWeight:900, fontSize:16, color:"#431407" };
const userPhone  = { fontSize:12, color:"#64748b" };
const blockedBadge = { display:"inline-block", fontSize:10, fontWeight:800, color:"#dc2626", background:"#fef2f2", padding:"2px 8px", borderRadius:10, border:"1px solid #fca5a5", width:"fit-content" };
const metricsRow = { display:"flex", flexWrap:"wrap", gap:8, marginBottom:8 };
const metricBox  = { background:"#f8fafc", borderRadius:8, padding:"8px 12px", minWidth:90 };
const flagsRow   = { display:"flex", flexWrap:"wrap", gap:6, marginTop:6 };
const flagChip   = { display:"inline-flex", alignItems:"center", gap:4, fontSize:11, fontWeight:700, color:"#92400e", background:"#fffbeb", padding:"3px 10px", borderRadius:20, border:"1px solid #fcd34d" };
const expandedPanel = { marginTop:12, borderTop:"1px solid #f1f5f9", paddingTop:12 };
const expandGrid = { display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))", gap:16 };
const expandSection = { background:"#f8fafc", borderRadius:8, padding:12 };
const secTitle   = { fontWeight:800, fontSize:13, color:"#ea580c", marginBottom:8 };
const viewBtn    = { display:"inline-flex", alignItems:"center", gap:4, padding:"6px 12px", borderRadius:8, background:"#ea580c", color:"#fff", border:"none", cursor:"pointer", fontSize:12, fontWeight:700 };
const expandBtn  = { padding:"6px 12px", borderRadius:8, background:"#f1f5f9", color:"#334155", border:"none", cursor:"pointer", fontSize:12, fontWeight:700 };
const refreshBtn = { display:"inline-flex", alignItems:"center", padding:"8px 14px", borderRadius:8, background:"#ea580c", color:"#fff", border:"none", cursor:"pointer", fontSize:13, fontWeight:700 };
const sel        = { padding:"8px 12px", borderRadius:8, border:"1px solid #e2e8f0", fontSize:13, color:"#334155", background:"#fff" };
